import { useState } from 'react';
import { Navigate } from 'react-router';
import { Badge, TooltipProvider } from '@monksflow/monks-ui';
import { EyeIcon, FoldersIcon, SparklesIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CollectionFilesPane } from '@/features/knowledge-bases/ui/CollectionFilesPane';
import { CollectionFormSheet } from '@/features/knowledge-bases/ui/CollectionFormSheet';
import { KnowledgeBaseModal } from '@/features/knowledge-bases/ui/KnowledgeBaseModal';
import {
  VISIBILITY_ICONS,
  getCollectionVisibilityLabel,
  resolveCollectionVisibility,
} from '@/features/knowledge-bases/lib';
import type { KnowledgeBaseSpaceVisibility } from '@/features/knowledge-bases/model';
import { useIsCollectionsRailCollapsed } from '@/features/knowledge-bases/model/collections-rail.store';
import { BreadcrumbPageHeader, NavigationPanel } from '@/shared/ui/layout';
import { AboutPageDialog } from '@/shared/ui';
import { PanelReveal } from '@/shared/ui/PanelReveal';
import { ROUTES } from '@/shared/config/routes';
import { CollectionsPaneLoading } from './CollectionsPaneLoading';
import { EmptyCollectionsPane } from './EmptyCollectionsPane';
import { useKnowledgeFilesWorkspace } from './hooks/useKnowledgeFilesWorkspace';

/**
 * Shell for the collections `NavigationPanel` and the pane of the collection it
 * opens. The rail's state (collection list, visibility filter) lives here, above the
 * pane, which is remounted by `key` so its own page/search/sort state resets
 * on every collection switch instead of needing reset effects.
 *
 * The bare route never rests on itself: as soon as the rail has a first
 * collection it hands off to that collection rather than asking anyone to
 * pick, so the pane region is only ever empty when there is nothing to open.
 */
export function KnowledgeFilesWorkspace() {
  const { t } = useTranslation('knowledge');
  const {
    collections,
    isLoadingCollections,
    activeCollectionId,
    spaceVisibility,
    onSpaceVisibilityChange,
    canCreate,
    onCreate,
    hrefFor,
  } = useKnowledgeFilesWorkspace();
  const isRailCollapsed = useIsCollectionsRailCollapsed();
  const [isAboutOpen, setIsAboutOpen] = useState(false);

  if (!activeCollectionId && collections.length > 0) {
    return <Navigate to={hrefFor(collections[0].id)} replace />;
  }

  const items = collections.map((collection) => {
    const visibility = resolveCollectionVisibility(collection);
    const VisibilityIcon = VISIBILITY_ICONS[visibility];

    return {
      id: collection.id,
      label: collection.name ?? t('knowledgeBases.knowledgeBaseDocumentsTable.untitled'),
      // The open collection carries the same badge in its pane header, so the row drops
      // it rather than saying it twice. The trailing slot collapses when it is empty, so
      // the name keeps its place whichever row is open.
      trailing:
        collection.id === activeCollectionId ? undefined : (
          <Badge variant="secondary" size="sm" roundness="full" className="translate-x-md scale-75">
            <VisibilityIcon aria-hidden="true" data-testid="collection-visibility-icon" />
            {getCollectionVisibilityLabel(t, collection)}
          </Badge>
        ),
    };
  });
  const emptyMessage =
    spaceVisibility === 'all'
      ? t('knowledgeBases.collectionsPanel.noCollectionsYet')
      : t('knowledgeBases.collectionsPanel.noCollectionsInThisSpace');

  const rail = (
    <NavigationPanel
      data-testid="collections-panel"
      title={t('knowledgeBases.collectionsPanel.collections')}
      items={items}
      activeItemId={activeCollectionId}
      isLoading={isLoadingCollections}
      emptyMessage={emptyMessage}
      createLabel={t('knowledgeBases.collectionsPanel.newCollection')}
      canCreate={canCreate}
      onCreate={onCreate}
      hrefFor={hrefFor}
      filters={{
        label: t('knowledgeBases.collectionsPanel.filterCollections'),
        groups: [
          {
            label: t('knowledgeBases.collectionsPanel.filterByVisibility'),
            value: spaceVisibility,
            options: [
              { id: 'all', label: t('knowledgeBases.collectionsPanel.all') },
              { id: 'internal', label: t('knowledgeBases.collectionsPanel.foundation') },
              {
                id: 'organization',
                label: t('knowledgeBases.collectionsPanel.organization'),
              },
              { id: 'workspace', label: t('knowledgeBases.collectionsPanel.workspace') },
            ],
            onChange: (id) => onSpaceVisibilityChange(id as KnowledgeBaseSpaceVisibility),
          },
        ],
      }}
    />
  );

  const paneContent = activeCollectionId ? (
    <CollectionFilesPane key={activeCollectionId} />
  ) : isLoadingCollections ? (
    <CollectionsPaneLoading />
  ) : (
    <EmptyCollectionsPane title={emptyMessage} />
  );

  return (
    <TooltipProvider>
      <div data-testid="knowledge-files-workspace" className="flex h-full min-h-0 flex-col gap-xs">
        <KnowledgeBaseModal renderForm={false} />
        <CollectionFormSheet />
        <BreadcrumbPageHeader
          trail={[
            {
              label: t('knowledgeBases.knowledgeFilesHeader.knowledge'),
              href: ROUTES.KNOWLEDGE_HUB,
            },
          ]}
          page={t('knowledgeBases.knowledgeFilesHeader.knowledgeFiles')}
          info={{ onClick: () => setIsAboutOpen(true) }}
        />
        <AboutPageDialog
          open={isAboutOpen}
          onOpenChange={setIsAboutOpen}
          title={t('knowledgeBases.knowledgeFilesAbout.title')}
          description={t('knowledgeBases.knowledgeFilesAbout.description')}
          rows={[
            { icon: FoldersIcon, text: t('knowledgeBases.knowledgeFilesAbout.groupFiles') },
            { icon: SparklesIcon, text: t('knowledgeBases.knowledgeFilesAbout.feedAgents') },
            { icon: EyeIcon, text: t('knowledgeBases.knowledgeFilesAbout.chooseReach') },
          ]}
        />
        <div data-testid="knowledge-files-workspace-panels" className="flex min-h-0 flex-1">
          <PanelReveal
            isOpen={!isRailCollapsed}
            axis="width"
            gapEdge="right"
            fadeOnClose
            className="flex shrink-0"
          >
            {rail}
          </PanelReveal>
          {paneContent}
        </div>
      </div>
    </TooltipProvider>
  );
}

KnowledgeFilesWorkspace.displayName = 'KnowledgeFilesWorkspace';
